import {
  buildAdminUsersListQuery,
  parseAdminUsersListResponse,
  ADMIN_USER_KINDS,
} from './adminUsersListApi';

const apiUrl = process.env.REACT_APP_API_URL;

function getBasicAuthHeader() {
  const username = process.env.REACT_APP_API_USERNAME;
  const password = process.env.REACT_APP_API_PASSWORD;
  return `Basic ${btoa(`${username}:${password}`)}`;
}

async function readJson(response) {
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Request failed: ${response.status}`);
  }
  return response.json();
}

/**
 * Org-scoped paginated list for Admin → Users.
 */
export async function fetchOrgAdminUsers({
  adminUserId,
  orgId,
  kind = ADMIN_USER_KINDS.instructor,
  page = 0,
  size = 10,
  keyword = '',
}) {
  const query = buildAdminUsersListQuery({
    adminUserId,
    orgId,
    kind,
    page,
    size,
    keyword,
  });
  const response = await fetch(`${apiUrl}/api/org/members?${query}`, {
    method: 'GET',
    headers: {
      Accept: '*/*',
      Authorization: getBasicAuthHeader(),
    },
  });
  const payload = await readJson(response);
  return parseAdminUsersListResponse(payload);
}

/**
 * Platform-wide paginated list for System → Application users.
 */
export async function fetchApplicationUsers({
  adminUserId,
  kind = ADMIN_USER_KINDS.instructor,
  page = 0,
  size = 10,
  keyword = '',
}) {
  const query = buildAdminUsersListQuery({
    adminUserId,
    kind,
    page,
    size,
  });
  const params = new URLSearchParams(query);
  if (keyword && keyword.trim()) {
    params.set('keyword', keyword.trim());
  }
  const response = await fetch(`${apiUrl}/api/system/users?${params.toString()}`, {
    method: 'GET',
    headers: {
      Accept: '*/*',
      Authorization: getBasicAuthHeader(),
    },
  });
  const payload = await readJson(response);
  return parseAdminUsersListResponse(payload);
}

/** Read-only org instructor/admin detail. */
export async function fetchOrgMemberDetail({ adminUserId, orgId, userId }) {
  const params = new URLSearchParams({ adminUserId, orgId });
  const response = await fetch(`${apiUrl}/api/org/members/${userId}?${params.toString()}`, {
    method: 'GET',
    headers: {
      Accept: '*/*',
      Authorization: getBasicAuthHeader(),
    },
  });
  return readJson(response);
}

/** Read-only org learner detail. */
export async function fetchOrgLearnerDetail({ adminUserId, orgId, studentId }) {
  const params = new URLSearchParams({ adminUserId, orgId });
  const response = await fetch(`${apiUrl}/api/org/members/learners/${studentId}?${params.toString()}`, {
    method: 'GET',
    headers: {
      Accept: '*/*',
      Authorization: getBasicAuthHeader(),
    },
  });
  return readJson(response);
}

/** Read-only platform user detail. */
export async function fetchApplicationUserDetail({ adminUserId, userId }) {
  const params = new URLSearchParams({ adminUserId });
  const response = await fetch(`${apiUrl}/api/system/users/${userId}?${params.toString()}`, {
    method: 'GET',
    headers: {
      Accept: '*/*',
      Authorization: getBasicAuthHeader(),
    },
  });
  return readJson(response);
}

/** Enables or disables a platform user account (super admin only). */
export async function updateApplicationUserActive({ adminUserId, userId, active }) {
  const response = await fetch(`${apiUrl}/api/system/users/${userId}/active`, {
    method: 'PATCH',
    headers: {
      Accept: '*/*',
      'Content-Type': 'application/json',
      Authorization: getBasicAuthHeader(),
    },
    body: JSON.stringify({
      adminUserId,
      active: active === 1 ? 1 : 0,
    }),
  });
  return readJson(response);
}

async function patchMemberStatus(path, { adminUserId, orgId, active }) {
  const response = await fetch(`${apiUrl}${path}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Accept: '*/*',
      Authorization: getBasicAuthHeader(),
    },
    body: JSON.stringify({
      adminUserId,
      orgId,
      active: active === 1 ? 1 : 0,
    }),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Request failed: ${response.status}`);
  }
  return response.json();
}

/** Enable or disable org instructor/admin membership. */
export async function updateOrgInstructorActive({ adminUserId, orgId, userId, active }) {
  return patchMemberStatus(`/api/org/members/${userId}/active`, {
    adminUserId,
    orgId,
    active,
  });
}

/** Enable or disable all org-course enrollments for a learner. */
export async function updateOrgLearnerActive({ adminUserId, orgId, studentId, active }) {
  return patchMemberStatus(`/api/org/members/learners/${studentId}/active`, {
    adminUserId,
    orgId,
    active,
  });
}
